import React, { useCallback, useEffect, useRef, useState } from 'react';
import Toolbar, { Item } from 'devextreme-react/toolbar';
import DropDownButton from 'devextreme-react/drop-down-button';
import { LoadIndicator } from 'devextreme-react/load-indicator';
import AntProcessingButton from './AntProcessingButton';
import AntLocalization from '../utils/AntLocalization';

export default function AntGridRowActions({ inlineActions, overflowActions, rowData, onRowAction, menuWidth }) {
    const [pendingAction, setPendingAction] = useState(null);
    const inFlight = useRef(false);
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    const runAction = useCallback(async ({ actionId }) => {
        if (inFlight.current) return;
        inFlight.current = true;
        setPendingAction(actionId);
        try {
            return await onRowAction({ actionId, data: rowData });
        } finally {
            inFlight.current = false;
            if (mounted.current) setPendingAction(null);
        }
    }, [onRowAction, rowData]);

    const menuIsProcessing = overflowActions.some(action => action.id === pendingAction);

    return (
        <Toolbar className="ant-grid-cell-buttons">
            {inlineActions.map((item, i) => (
                <Item key={i}>
                    <AntProcessingButton
                        actionId={item.id}
                        type={item.type}
                        stylingMode={item.stylingMode}
                        icon={item.icon}
                        data={rowData}
                        text={item.text}
                        width={item.width}
                        disabled={Boolean(pendingAction) && pendingAction !== item.id}
                        onClick={runAction}
                    />
                </Item>
            ))}
            {overflowActions.length > 0 && (
                <Item>
                    {menuIsProcessing ? (
                        <div className="ant-grid-row-actions-pending" role="status" aria-label={AntLocalization.translateText({
                            vi: 'Đang xử lý', en: 'Processing', fr: 'Traitement', es: 'Procesando', id: 'Memproses',
                            ja: '処理中', zh: '处理中', ko: '처리 중', hi: 'प्रक्रिया जारी', ar: 'جارٍ التنفيذ'
                        })}>
                            <LoadIndicator visible width={18} height={18} />
                        </div>
                    ) : (
                        <DropDownButton
                            className="ant-grid-row-actions-more"
                            icon="overflow"
                            hint={AntLocalization.translateText({ vi: 'Thao tác khác', en: 'More actions', fr: 'Autres actions', es: 'Más acciones', id: 'Aksi lainnya', ja: 'その他の操作', zh: '更多操作', ko: '추가 작업', hi: 'अन्य क्रियाएँ', ar: 'إجراءات أخرى' })}
                            items={overflowActions}
                            keyExpr="id"
                            displayExpr="text"
                            showArrowIcon={false}
                            stylingMode="text"
                            width={menuWidth}
                            disabled={Boolean(pendingAction)}
                            dropDownOptions={{ width: 280 }}
                            itemRender={(item) => (
                                <div className="ant-grid-row-action-menu-item">
                                    {item.icon && <i className={`dx-icon dx-icon-${item.icon}`} />}
                                    <span>{item.text}</span>
                                </div>
                            )}
                            onItemClick={(e) => {
                                if (e.event) e.event.stopPropagation();
                                // The API/consumer reports failures; avoid an unhandled event-handler rejection.
                                runAction({ actionId: e.itemData.id }).catch(() => {});
                            }}
                        />
                    )}
                </Item>
            )}
        </Toolbar>
    );
}
